import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Bot, User, MessageSquare, Loader2 } from 'lucide-react';

export default function ChatInterface() {
  const { toast } = useToast();
  
  // Initialize with empty arrays to prevent .length undefined errors
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  const messagesEndRef = useRef(null);

  // 1. Memorized Fetch Functions
  const fetchContacts = useCallback(async () => {
    try {
      const response = await api.get('/ai/conversations');
      
      /** * ARCHITECTURE FIX: 
       * MongoDB API returns { success: true, data: [...], meta: {...} }
       */
      if (response.data.success) {
        // We look for .data or .contacts, defaulting to an empty array
        const contactList = response.data.data || response.data.contacts || [];
        setContacts(contactList);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load history.', variant: 'destructive' });
    } finally {
      setLoadingContacts(false);
    }
  }, [toast]);

  const fetchThread = useCallback(async (contactNumber) => {
    setLoadingMessages(true);
    try {
      const response = await api.get(`/ai/conversations/${contactNumber}`);
      
      if (response.data.success) {
        // Safely extract the thread array
        const thread = response.data.thread || response.data.data || [];
        setMessages(thread);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load messages.', variant: 'destructive' });
    } finally {
      setLoadingMessages(false);
    }
  }, [toast]);

  // 2. Lifecycle Hooks
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    if (selectedContact) {
      fetchThread(selectedContact);
    }
  }, [selectedContact, fetchThread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="p-8 max-w-7xl mx-auto h-[calc(100vh-4rem)] flex flex-col">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-900">
          <MessageSquare className="h-8 w-8 text-indigo-600" /> AI Conversations
        </h1>
        <p className="text-muted-foreground">Monitor the interactions between your contacts and your AI assistant.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-grow min-h-0">
        
        {/* LEFT PANE: Contact List */}
        <Card className="col-span-1 flex flex-col min-h-0 shadow-sm border-none ring-1 ring-slate-200">
          <CardHeader className="py-4 border-b bg-slate-50/50">
            <CardTitle className="text-lg">Recent Chats</CardTitle>
          </CardHeader>
          <ScrollArea className="flex-grow">
            {loadingContacts ? (
              <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>
            ) : contacts?.length > 0 ? ( // Added safe navigation
              <div className="flex flex-col">
                {contacts.map((contact) => (
                  <button
                    key={contact.contact_number}
                    onClick={() => setSelectedContact(contact.contact_number)}
                    className={`p-4 text-left border-b hover:bg-slate-50 transition-colors ${
                      selectedContact === contact.contact_number ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                    }`}
                  >
                    <div className="font-semibold text-slate-800">{contact.contact_number}</div>
                    <div className="text-xs text-muted-foreground flex justify-between mt-1">
                      <span>{contact.last_interaction ? new Date(contact.last_interaction).toLocaleDateString() : 'N/A'}</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {contact.total_tokens || 0} tokens
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground text-sm">
                No AI conversations found.
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* RIGHT PANE: Chat Thread */}
        <Card className="col-span-1 md:col-span-2 flex flex-col min-h-0 shadow-md border-t-4 border-t-indigo-600 border-x-0 border-b-0">
          {selectedContact ? (
            <>
              <CardHeader className="py-4 border-b bg-white flex-shrink-0">
                <CardTitle className="flex justify-between items-center text-lg">
                  <span>Chat with {selectedContact}</span>
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 shadow-none">
                    <Bot className="w-3 h-3 mr-1" /> AI Managed
                  </Badge>
                </CardTitle>
              </CardHeader>
              <ScrollArea className="flex-grow p-4 bg-slate-50/50">
                {loadingMessages ? (
                  <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
                ) : (
                  <div className="space-y-4">
                    {/* Safe mapping with messages array */}
                    {messages?.map((msg, index) => {
                      const isAi = msg.role === 'assistant';
                      return (
                        <div key={index} className={`flex w-full ${isAi ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex max-w-[75%] gap-2 ${isAi ? 'flex-row-reverse' : 'flex-row'}`}>
                            
                            <div className="flex-shrink-0 mt-auto">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAi ? 'bg-indigo-600 shadow-md' : 'bg-slate-200'}`}>
                                {isAi ? <Bot className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-slate-600" />}
                              </div>
                            </div>

                            <div className={`p-3 rounded-2xl shadow-sm relative ${
                              isAi 
                                ? 'bg-indigo-600 text-white rounded-br-none' 
                                : 'bg-white border border-slate-200 text-slate-800 rounded-bl-none'
                            }`}>
                              <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                              
                              <div className={`text-[10px] mt-1 flex justify-end gap-2 ${isAi ? 'text-indigo-200' : 'text-slate-400'}`}>
                                <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                            
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8 text-center bg-slate-50/30">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-indigo-300" />
              </div>
              <p className="text-lg font-medium text-slate-600">Select a conversation</p>
              <p className="text-sm mt-1">Select a contact to view the AI history.</p>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}